import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import SublyApi from '../helpers/Api';
import { filterObject } from '../helpers/helperFunctions';
import { startImpersonation, stopImpersonation, adminLogout } from './admin';
import { MAX_ACCOUNTS, ACCOUNT_CAP_MESSAGE } from '../constants/accounts';

/** info we don't want in redux */
const privateUserInfo = ['notes'];

/** Multi-account (#168): may a new sign-in / sign-up for `username` proceed?
 * Blocks the network call entirely at the cap, unless the username is already
 * signed in (re-adding an existing account just re-activates it).
 * Usernames are compared exactly — never lowercased (#165 is deferred). */
export const canAddAccount = (userState, username) => {
	const accounts = Array.isArray(userState?.accounts) ? userState.accounts : [];
	return accounts.length < MAX_ACCOUNTS || accounts.some((a) => a.username === username);
};

const accountCapCondition = (data, { getState }) =>
	canAddAccount(getState().user, data?.username);
// First, create the thunk
export const userSignup = createAsyncThunk(
	'user/userSignup',
	async (data, { rejectWithValue }) => {
		try {
			const { token, user } = await SublyApi.userSignup(data);

			//create customer from stripe, update user with customer id, and reload user data into storage.
			const name = `${user.firstName} ${user.lastName}`;
			const customerId = await SublyApi.createCustomer(user.email, name, token);
			const response = await SublyApi.updateUser(
				user.username,
				{ customerId },
				token
			);

			console.log(response.token);

			// send email to admin to notify of new user
			await SublyApi.sendNewUserEmail({
				token: token,
			});

			// delete sensitive information
			filterObject(response.user, privateUserInfo);

			// add token to user response
			response.token = token;

			console.log(response);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
	{ condition: accountCapCondition }
);

// Thunk for user login
export const userLogin = createAsyncThunk(
	'user/userLogin',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.userLogin(data);

			// delete sensitive information
			filterObject(response.user, privateUserInfo);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
	{ condition: accountCapCondition }
);
// Thunk for getting user
export const getUser = createAsyncThunk(
	'user/getUser',
	async (data, { rejectWithValue, getState }) => {
		try {
			const response = await SublyApi.getUser(data.username, data.token);

			// delete sensitive information
			filterObject(response.user, privateUserInfo);

			// Preserve impersonated token — the API returns a fresh regular
			// token that would overwrite the impersonated one, breaking write-blocking
			const { admin } = getState();
			if (admin?.isImpersonating) {
				response._preserveToken = true;
			}

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

// Thunk for updating user
export const updateUser = createAsyncThunk(
	'user/updateUser',
	async (data, { rejectWithValue }) => {
		try {
			// throw [ 'This is an error' ];
			const response = await SublyApi.updateUser(
				data.username,
				data.data,
				data.token
			); //note: token only given when verified status is updated

			// delete sensitive information
			filterObject(response.user, privateUserInfo);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

// Thunk for user verification
export const changeToVerified = createAsyncThunk(
	'user/changeToVerified',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.updateUser(
				data.username,
				{ verified: true },
				data.token
			);

			await SublyApi.sendWelcomeEmail({
				username: data.username,
				token: response.token,
			});

			// delete sensitive information
			filterObject(response.user, privateUserInfo);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

//Thunk for updating images
export const updateImages = createAsyncThunk(
	'user/updateImages',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.updateImages(
				data.username,
				data.data,
				data.token
			);

			// delete sensitive information
			filterObject(response.user, privateUserInfo);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

/** Function for retrieving and storing user's stripe subscriptions
 *
 * This will get all products corresponding to user's active subscriptions on the Jane Rothe Stripe account
 *
 * response === {stripeSubs: [{stripe price object}, {stripe price object}]}
 */
export const getUserSubs = createAsyncThunk(
	'user/getUserSubs',
	async ({ customerId, token }, { rejectWithValue }) => {
		try {
			const response = await SublyApi.getStripeSubscriptions(customerId, token);
			return response;
		} catch (err) {
			return rejectWithValue(err);
		}
	}
);

/** Gets all users from API. */

export const getAllUsers = createAsyncThunk(
	'user/getAllUsers',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.getUsers(data.token, data.status);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

/** Gets all subs from API*/

export const getAllSubs = createAsyncThunk(
	'user/getAllSubs',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.getAllStripeSubscriptions(data.token);

			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

/** Gets subscription events from API for historical MRR calculation*/
export const getSubscriptionEvents = createAsyncThunk(
	'user/getSubscriptionEvents',
	async (data, { rejectWithValue }) => {
		try {
			const response = await SublyApi.getSubscriptionEvents(data.token, data.days);
			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

/** Gets user's form responses Typeform API
 * data = {username, token, formsPackage}
 */

export const getTypeforms = createAsyncThunk(
	'user/getTypeforms',
	async (data, { rejectWithValue }) => {
		try {
			const typeforms = {};
			const { username, token, formPackage } = data;

			for (let i = 0; i < formPackage.length; i++) {
				const response = await SublyApi.getTypeformResponses({
					username,
					formId: formPackage[i].id,
					token,
				});
				typeforms[formPackage[i].name] = response.items;
			}

			return { typeforms };
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const initialState = {
	allUsers: [],
	currentUser: {},
	allSubs: [],
	stripeSubs: [],
	subscriptionEvents: [],
	typeforms: {},
	token: null,
	loading: false,
	errors: [],
	success: false, // monitors the success of an api call, in case it needs to be passed to a component
	remember: true, // controls whether a user's info is stored after dismount
	pending: false, // !note: same as loading. just a duplicate
	newRes: false, // monitors Typeforn webhook for new responses
	recentCheckout: false,
	// Multi-account session (#168). `currentUser`/`token` above remain the live
	// projection of the active account so existing consumers keep working.
	accounts: [], // [{ username, token, currentUser, remember }] — keyed by EXACT username
	activeUsername: null,
	impersonating: false, // true while an admin impersonation owns the projection
};

const accountsOf = (state) => (Array.isArray(state.accounts) ? state.accounts : []);

/** Per-user caches that must not leak from one account to another on switch. */
const clearAccountCaches = (state) => {
	state.stripeSubs = [];
	state.typeforms = {};
	state.recentCheckout = false;
	state.newRes = false;
	state.success = false;
	state.errors = [];
};

const projectAccount = (state, entry) => {
	state.currentUser = entry.currentUser;
	state.token = entry.token;
	state.activeUsername = entry.username;
};

/** Add (or refresh) an account and make it active. Case-sensitive dedupe.
 * Returns false when refused at the cap. */
const upsertAndActivate = (state, user, token) => {
	if (!user || !user.username) return false;
	// An admin impersonation owns the projection; never mix a real account into it.
	if (state.impersonating) return false;
	const accounts = accountsOf(state);
	const idx = accounts.findIndex((a) => a.username === user.username);
	if (idx === -1 && accounts.length >= MAX_ACCOUNTS) {
		state.errors = [ACCOUNT_CAP_MESSAGE];
		return false;
	}
	const entry = {
		username: user.username,
		token,
		currentUser: user,
		remember: state.remember !== false,
	};
	state.accounts =
		idx === -1 ? [...accounts, entry] : accounts.map((a, i) => (i === idx ? entry : a));
	if (state.activeUsername !== user.username) clearAccountCaches(state);
	projectAccount(state, entry);
	return true;
};

/** Full account reset that leaves an in-progress impersonation view intact. */
const keepImpersonatedView = (state) => ({
	...initialState,
	currentUser: state.currentUser,
	token: state.token,
	impersonating: true,
});

/** Restore the projection from the active account, or empty it if none. */
const restoreActiveProjection = (state) => {
	const active = accountsOf(state).find((a) => a.username === state.activeUsername);
	if (active) {
		state.currentUser = active.currentUser;
		state.token = active.token;
	} else {
		state.currentUser = {};
		state.token = null;
	}
};

export const userSlice = createSlice({
	name: 'user',
	initialState: initialState,
	reducers: {
		/** Sign out of ALL accounts — full reset (today's Logout). */
		//  While an admin impersonation owns the projection, the impersonated view
		//  is kept: only stopImpersonation / adminLogout may end it.
		userLogout: (state, action) => {
			return state.impersonating ? keepImpersonatedView(state) : initialState;
		},
		/** Make an already-signed-in account active (no re-login). */
		switchAccount: (state, action) => {
			if (state.impersonating) return;
			const entry = accountsOf(state).find((a) => a.username === action.payload);
			if (!entry || entry.username === state.activeUsername) return;
			clearAccountCaches(state);
			projectAccount(state, entry);
		},
		/** Sign out of ONE account; re-point to a remaining one, or full reset. */
		removeAccount: (state, action) => {
			const accounts = accountsOf(state);
			if (!accounts.some((a) => a.username === action.payload)) return;
			const remaining = accounts.filter((a) => a.username !== action.payload);
			if (remaining.length === 0) {
				return state.impersonating ? keepImpersonatedView(state) : initialState;
			}
			state.accounts = remaining;
			if (state.activeUsername === action.payload) {
				clearAccountCaches(state);
				if (state.impersonating) state.activeUsername = remaining[0].username;
				else projectAccount(state, remaining[0]);
			}
		},
		clearSuccess: (state, action) => {
			state.success = false;
		},
		clearErrors: (state, action) => {
			state.errors = [];
		},
		addToCurrentUser: (state, action) => {
			state.currentUser = { ...state.currentUser, ...action.payload };
		},
		changeRemember: (state, action) => {
			state.remember = action.payload;
		},
		changeNewRes: (state, action) => {
			state.newRes = action.payload;
		},
		addErrors: (state, action) => {
			state.errors = Array.isArray(action.payload)
				? action.payload
				: [action.payload];
		},
		setRecentCheckout: (state, action) => {
			state.recentCheckout = action.payload;
		},
	},

	extraReducers: (builder) => {
		builder
			//* User Signup
			.addCase(userSignup.fulfilled, (state, action) => {
				state.errors = [];
				state.pending = false;
				upsertAndActivate(state, action.payload.user, action.payload.token);
			})
			.addCase(userSignup.pending, (state, action) => {
				state.errors = [];
				state.pending = true;
			})
			.addCase(userSignup.rejected, (state, action) => {
				state.errors = Array.isArray(action.payload)
					? action.payload
					: [action.payload];
				state.pending = false;
			})
			//* User Login
			.addCase(userLogin.fulfilled, (state, action) => {
				state.errors = [];
				state.pending = false;
				upsertAndActivate(state, action.payload.user, action.payload.token);
			})
			.addCase(userLogin.rejected, (state, action) => {
				state.errors = action.payload;
				state.pending = false;
			})
			.addCase(userLogin.pending, (state, action) => {
				state.errors = [];
				state.pending = true;
			})
			//* Get User
			.addCase(getUser.fulfilled, (state, action) => {
				state.currentUser = action.payload.user;
				// During impersonation, preserve the impersonated token.
				// The API returns a fresh non-impersonated token that would
				// break write-blocking if allowed to overwrite.
				if (!action.payload._preserveToken) {
					state.token = action.payload.token;
				}
				state.errors = [];
			})
			.addCase(getUser.rejected, (state, action) => {
				state.errors = action.payload;
			})
			//* Get All Users
			.addCase(getAllUsers.fulfilled, (state, action) => {
				state.allUsers = action.payload.users;
				// state.token = action.payload.token;
				state.errors = [];
			})
			.addCase(getAllUsers.rejected, (state, action) => {
				state.errors = action.payload;
			})
			//* Update User
			.addCase(updateUser.fulfilled, (state, action) => {
				state.currentUser = action.payload.user;
				state.errors = [];
				state.success = true;
			})
			.addCase(updateUser.rejected, (state, action) => {
				state.errors = action.payload;
			})
			//* Change to Verified
			.addCase(changeToVerified.fulfilled, (state, action) => {
				state.currentUser = action.payload.user;
				state.token = action.payload.token;
				state.errors = [];
				// Verification logs the user in when no account session exists yet.
				if (!state.impersonating && accountsOf(state).length === 0) {
					upsertAndActivate(state, action.payload.user, action.payload.token);
				}
			})
			.addCase(changeToVerified.rejected, (state, action) => {
				state.errors = action.payload;
			})
			//* Update Images
			.addCase(updateImages.fulfilled, (state, action) => {
				state.currentUser = action.payload.user;
				state.errors = [];
				state.success = true;
				state.loading = false;
			})
			.addCase(updateImages.pending, (state, action) => {
				state.loading = true;
			})
			.addCase(updateImages.rejected, (state, action) => {
				state.errors = action.payload;
				state.loading = false;
			})
			//* Get User Subs
			.addCase(getUserSubs.fulfilled, (state, action) => {
				// Drop a late response that belongs to a previously active account.
				if (action.meta?.arg?.customerId !== state.currentUser?.customerId) return;
				state.stripeSubs = action.payload.stripeSubs;
				state.errors = [];
			})
			.addCase(getUserSubs.rejected, (state, action) => {
				state.errors = action.payload;
			})
			//* Get typeforms
			.addCase(getTypeforms.fulfilled, (state, action) => {
				if (action.meta?.arg?.username !== state.currentUser?.username) return;
				state.typeforms = action.payload.typeforms;
				state.errors = [];
			})
			.addCase(getTypeforms.rejected, (state, action) => {
				state.errors = action.payload;
			})
			//* Get all Subs
			.addCase(getAllSubs.fulfilled, (state, action) => {
				state.allSubs = action.payload.stripeSubs;
				state.errors = [];
			})
			.addCase(getAllSubs.rejected, (state, action) => {
				state.errors = action.payload;
			})
			//* Get subscription events
			.addCase(getSubscriptionEvents.fulfilled, (state, action) => {
				state.subscriptionEvents = action.payload.events;
				state.errors = [];
			})
			.addCase(getSubscriptionEvents.rejected, (state, action) => {
				state.errors = action.payload;
			})
			//* Impersonation - sync user state when admin starts impersonation
			//  The impersonated session owns the projection only; it is never an account.
			.addCase(startImpersonation.fulfilled, (state, action) => {
				const { user, token } = action.payload;
				state.impersonating = true;
				state.currentUser = user;
				state.token = token;
			})
			//* Impersonation - end: restore the active account's session if one was
			//  signed in on this browser, else clear as before.
			.addCase(stopImpersonation.fulfilled, (state) => {
				state.impersonating = false;
				restoreActiveProjection(state);
			})
			.addCase(stopImpersonation.rejected, (state) => {
				state.impersonating = false;
				restoreActiveProjection(state);
			})
			//* Admin logout mid-impersonation must not leave the impersonated
			//  session behind in the user slice.
			.addCase(adminLogout, (state) => {
				if (!state.impersonating) return;
				state.impersonating = false;
				restoreActiveProjection(state);
			});
	},
});

export const {
	userLogout,
	switchAccount,
	removeAccount,
	clearSuccess,
	clearErrors,
	changeRemember,
	changeNewRes,
	addErrors,
	setRecentCheckout,
} = userSlice.actions;

/** Keep the active account entry in sync with the projection (#168).
 *
 * The reducers that write `currentUser`/`token` stay as they were; this
 * post-step routes their writes into the account list:
 *  - projection is the active account → copy it into the active entry;
 *  - projection is ANOTHER signed-in account (a late response that landed after
 *    a switch) → store it in that entry, then restore the active projection;
 *  - anything else (e.g. a late response for a signed-out account) → restore
 *    the active projection, so an evicted account cannot come back.
 * Skipped during admin impersonation and when no accounts exist. */
export const reconcileAccounts = (prev, next) => {
	if (!next || next.impersonating) return next;
	const accounts = accountsOf(next);
	if (accounts.length === 0) return next;
	const active = accounts.find((a) => a.username === next.activeUsername);
	if (!active) return next;
	const projected = next.currentUser?.username;
	if (projected === next.activeUsername) {
		if (active.currentUser === next.currentUser && active.token === next.token) return next;
		return {
			...next,
			accounts: accounts.map((a) =>
				a === active ? { ...a, currentUser: next.currentUser, token: next.token } : a
			),
		};
	}
	// Only take the token along if this action actually wrote one.
	const tokenChanged = Boolean(prev) && next.token !== prev.token && Boolean(next.token);
	return {
		...next,
		accounts: accounts.map((a) =>
			projected && a.username === projected
				? { ...a, currentUser: next.currentUser, token: tokenChanged ? next.token : a.token }
				: a
		),
		currentUser: active.currentUser,
		token: active.token,
	};
};

export default function userReducer(state, action) {
	return reconcileAccounts(state, userSlice.reducer(state, action));
}
